package modysy.tools

import modysy.GameController
import modysy.datastructures.Vector2
import modysy.model.{Obstacle, _}
import modysy.view.{Color, ImageProvider}
import org.scalajs.dom

/** Implements a tool for placing obstacles with the given lifetime and collision health
  * into the game world controlled by the given GameController.
  *
  * @param gameController          the GameController controlling the game world
  * @param obstacleLifetime        lifetime of obstacles placed
  * @param obstacleCollisionHealth collision health of obstacles placed
  * @see Obstacle
  */
class ObstaclePlacementTool(
    override protected val gameController: GameController
) extends PlacementTool {

  override protected var currentOrientation: Double = 0

  /** A dummy object used to check for collisions with objects already
    * present in the level. */
  override protected val placedObject = new Obstacle()

  // previous rotation angle while rotating the obstacle
  private var touchRotatePreviousAngle: Option[Double] = None

  // if the tool is touched
  private var touchOnTool: Boolean = false

  /** Renders the icon of this tool onto the given rendering context.
    * The icon is drawn with its center at (0, 0) so the context
    * need to be translated correctly.
    *
    * @param ctx context to render the icon on
    */
  override def renderIcon(ctx: dom.CanvasRenderingContext2D): Unit = {
    ctx.save()
    ctx.scale(0.4, 0.4)
    ImageProvider
      .drawImgFnc(ImageProvider.getImage("box"), 100, 100)
      .apply(ctx, -50, -50)
    ctx.restore()
  }

  /** Renders the non-icon parts of this tool at the tools position set
    * by moveTo(...) or moveBy(...).
    * The tool must be positioned somewhere in the game world before.
    *
    * @param ctx context to render on
    */
  override def render(ctx: dom.CanvasRenderingContext2D): Unit = {
    require(
      _position.isDefined,
      "rendering but ObstaclePlacementTool not positioned"
    )

    ctx.save()
    ctx.globalAlpha = 0.5

    ctx.translate(_position.get.x, _position.get.y)
    ctx.rotate(currentOrientation)

    if (isPlaceable(_position.get)) {
      ImageProvider
        .drawImgFnc(ImageProvider.getImage("box"), 100, 100)
        .apply(ctx, -50, -50)
    } else {
      ctx.fillStyle = Color.Red
      ctx.fillRect(-50, -50, 100, 100)
      ImageProvider
        .drawImgFnc(ImageProvider.getImage("box"), 100, 100)
        .apply(ctx, -50, -50)
    }

    ctx.restore()
  }

  /** Uses this tool at the tools position set by moveTo(...) or moveBy(...).
    * The tool must be positioned somewhere in the game world before.
    *
    * Returns true if the tool was used successfully, otherwise false.
    *
    * @return Whether the interaction was successful.
    */
  override def use(): Boolean = {
    require(
      _position.isDefined,
      "called use() but ObstaclePlacementTool not positioned"
    )

    if (isPlaceable(_position.get)) {
      val gameObject: Obstacle =
        new Obstacle()
      gameObject.body.pose = Transform
        .translate(_position.get)
        .rotate(currentOrientation)
      gameController.addGameObject(gameObject)
      true
    } else false
  }

  /** Call this when the user starts to touch and touches the canvas at two or less spots simultaneously.
    *
    * If the tool is touched, then a second touch somewhere else will enable obstacle rotation.
    *
    * Returns true if the touch gesture has hit anything and therefore
    * should be treated as consumed. Otherwise returns false, indicating that this
    * gesture may trigger other actions (e.g. scrolling).
    *
    * @param touchWorldPos world position of the starting touch relative to the upper left corner of the world
    * @return true, if the touch event can be considered as consumed
    */
  def onTouchStart(touchWorldPos: Vector2): Boolean = {
    if (_position.isDefined) {
      if (isOver(touchWorldPos)) { // touch to the tool
        touchOnTool = true
      }
    }

    if (touchOnTool) { // tool already touched and second touch somewhere else
      require(_position.isDefined, "TouchOnTool but tool not positioned")

      val pos = _position.get // tools position
      val diff = (touchWorldPos - pos).unit // normalized vector from tool to touch pos

      touchRotatePreviousAngle = Some(Vector2(0, 1).dot(diff))
    }

    false
  }

  private def handleRotate(toolToTouch: Vector2): Unit = {
    val rotateCurrentAngle = Vector2(0, 1).dot(toolToTouch)

    currentOrientation += touchRotatePreviousAngle.get - rotateCurrentAngle
    touchRotatePreviousAngle = Some(rotateCurrentAngle)
  }

  /** Call this when the user moves his touch and touches to the canvas at exactly two spots simultaneously.
    *
    * If obstacle rotation was enabled via the gesture, then moving the finger not on the tool
    * will rotate the obstacle.
    *
    * Moving the finger touching the tool away will disable obstacle rotation.
    *
    * Returns true if the touch gesture has rotated the obstacle and therefore
    * should be treated as consumed. Otherwise returns false, indicating that this
    * gesture may trigger other actions (e.g. scrolling).
    *
    * @param touch1WorldPos world position of the first touch relative to the upper left corner of the world
    * @param touch2WorldPos world position of the second touch relative to the upper left corner of the world
    * @return true, if the touch event can be considered as consumed
    */
  def onTouchMove(touch1WorldPos: Vector2, touch2WorldPos: Vector2): Boolean = {
    if (touchRotatePreviousAngle.isDefined) {
      require(
        _position.isDefined,
        "touchRotatePreviousAngle defined but tool not positioned"
      )

      val pos = _position.get // tools position

      if (isOver(touch1WorldPos) && !isOver(touch2WorldPos)) {
        val diff = (touch2WorldPos - pos).unit // normalized vector from tool to touch pos
        handleRotate(diff)

        return true
      }

      if (isOver(touch2WorldPos) && !isOver(touch1WorldPos)) {
        val diff = (touch1WorldPos - pos).unit // normalized vector from tool to touch pos
        handleRotate(diff)

        return true
      }

      // moved away from the tool
      touchRotatePreviousAngle = None
      touchOnTool = false
    }

    false
  }

  /** Call this when the user ends all or all but one of his touches to the canvas.
    *
    * If obstacle rotation is enabled but the tool is not touched any longer, then it gets disabled.
    *
    * Always returns false indicating that this gesture may trigger other actions (e.g. scrolling).
    *
    * @param remainingTouchWorldPos world position of the remaining touch relative to the upper left corner of the world
    *                               or None
    * @return false, indicating that his gesture is not consumed
    */
  def onTouchEnd(remainingTouchWorldPos: Option[Vector2]): Boolean = {
    touchRotatePreviousAngle = None

    if (remainingTouchWorldPos.isDefined) {
      if (!isOver(remainingTouchWorldPos.get)) {
        touchOnTool = false
      }
    } else {
      touchOnTool = false
    }

    false
  }

  def rotateCW(): Unit = {
    currentOrientation += 2.0 / 180.0 * Math.PI
  }

  def rotateCCW(): Unit = {
    currentOrientation -= 2.0 / 180.0 * Math.PI
  }

  /** Returns the non-negative amount of energy points needed to
    * use this tool once. This amount of points will be spent
    * using this tool once.
    *
    * @return energy cost to use this tool once
    */
  override def cost: Int = 0

  override var uses: Int = 999

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/2d4fb5cd/0/mtv-pr-2018/modysy-product/src/main/scala/modysy/tools/ObstaclePlacementTool.scala